import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Modal } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAnimation } from '../../hooks/useAnimation';
import axios from 'axios';
import config from '../../config';
import './ContactPage.scss';

const ContactPage = () => {
  const { t } = useTranslation();
  const { fadeOnly, scaleIn } = useAnimation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Türkçe karakterleri korumak için özel işlem
    let processedValue = value;
    
    // Input validation ve encoding
    if (name === 'full_name' || name === 'message') {
      // Türkçe karakterleri koru
      processedValue = value.normalize('NFC');
    }
    
    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${config.API_URL}/contact-messages`, formData, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });
      
      if (response.data.success) {
        setShowSuccess(true);
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error:', error);
      alert(t('contact.error'));
    }
  };

  return (
    <section id="contact" className="contact-section">
      <Container>
        <Row className="align-items-center">
          <Col md={6}>
            <motion.div
              className="contact-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={fadeOnly}
            >
              <h2>{t('contact.title')}</h2>
              <p>
                {t('contact.description')}
              </p>
            </motion.div>
          </Col>
          <Col md={6}>
            <motion.div
              className="contact-form"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={scaleIn}
              transition={{ delay: 0.2 }}
            >
              <Form onSubmit={handleSubmit} encType="application/x-www-form-urlencoded">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('contact.form.full_name')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        placeholder={t('contact.form.full_name_placeholder')} 
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('contact.form.email')}</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.form.email_placeholder')} 
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('contact.form.phone')}</Form.Label>
                      <Form.Control 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.form.phone_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('contact.form.message')}</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={5} 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.form.message_placeholder')} 
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="d-flex justify-content-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                    >
                      {t('contact.form.submit')}
                    </button>
                  </Col>
                </Row>
              </Form>
            </motion.div>
          </Col>
        </Row>
      </Container>
      
      {/* Success Modal */}
      <Modal 
        show={showSuccess} 
        onHide={() => setShowSuccess(false)}
        centered
      >
        <Modal.Body className="text-center p-4">
          <FaCheckCircle size={50} color="#28a745" className="mb-3" />
          <h4>{t('contact.success.title')}</h4>
          <p>{t('contact.success.message')}</p>
          <button 
            className="btn btn-success mt-3" 
            onClick={() => setShowSuccess(false)}
          >
            {t('contact.success.close')}
          </button>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default ContactPage;